/**
 * Phase 5 — scoring comps against a subject property.
 *
 * Three tiers appear on the deal page, and they are three different kinds of
 * claim, so they never look alike:
 *
 *   used in underwriting   from our source files. Read-only, with the file and
 *                          sheet it came from. This is what the UW was built on.
 *   suggested              scored against the subject and ranked, with the
 *                          reason shown. Computed live, never stored as fact.
 *   hand-picked            somebody promoted a suggestion or added one. Stored,
 *                          survives a reload, badged as judgement.
 *
 * The score is shown WITH ITS REASONING rather than as a bare rank, because a
 * number nobody can interrogate is a number nobody should trust. Every
 * component below returns both a contribution and a sentence explaining it.
 */

import { haversineMiles } from "../supabase";

const clamp01 = (x) => Math.max(0, Math.min(1, x));

/** Size band of a suite, matching the bands used elsewhere on the page. */
export function sizeBand(sf) {
  const n = Number(sf) || 0;
  if (!n) return null;
  if (n < 5000) return "< 5k";
  if (n < 15000) return "5-15k";
  if (n < 50000) return "15-50k";
  return "50k+";
}

/**
 * Score one comp against the subject.
 *
 * Weights reflect what actually moves a rent in these markets: where it is and
 * when it signed dominate, size matters, and the physical specifics matter
 * least because they are the most often missing.
 */
export function scoreComp(comp, subject, { radiusMiles = 3 } = {}) {
  const reasons = [];
  let score = 0;

  // distance — 35 points
  const miles = haversineMiles(subject.lat, subject.lng, comp.latitude, comp.longitude);
  if (Number.isFinite(miles)) {
    const d = clamp01(1 - miles / radiusMiles);
    score += d * 35;
    reasons.push(miles < 0.5
      ? `${miles.toFixed(2)} mi away — effectively the same submarket`
      : `${miles.toFixed(2)} mi away`);
  } else {
    reasons.push("no coordinate, so distance could not be judged");
  }

  // recency — 30 points. A 2019 comp is not evidence for a 2026 rent.
  const year = Number(String(comp.comp_date || "").slice(0, 4));
  const now = new Date().getFullYear();
  if (year) {
    const age = now - year;
    const r = clamp01(1 - age / 5);
    score += r * 30;
    reasons.push(age <= 1 ? `signed ${year} — current`
      : age <= 3 ? `signed ${year}` : `signed ${year}, which is getting old`);
  } else {
    reasons.push("undated, so recency could not be judged");
  }

  // size band — 20 points
  const sb = sizeBand(comp.suite_sf), tb = sizeBand(subject.sf);
  if (sb && tb) {
    if (sb === tb) { score += 20; reasons.push(`same size band (${sb})`); }
    else {
      const order = ["< 5k", "5-15k", "15-50k", "50k+"];
      const gap = Math.abs(order.indexOf(sb) - order.indexOf(tb));
      score += gap === 1 ? 10 : 2;
      reasons.push(`${sb} against the subject's ${tb}`);
    }
  }

  // physical specifics — 15 points, the most often absent
  let phys = 0, physN = 0;
  if (comp.clear_height && subject.clearHeight) {
    physN += 1;
    const d = Math.abs(comp.clear_height - subject.clearHeight);
    phys += clamp01(1 - d / 12);
    reasons.push(`${comp.clear_height}' clear against ${subject.clearHeight}'`);
  }
  if (comp.door_type && subject.doorType) {
    physN += 1;
    const same = String(comp.door_type).toLowerCase() === String(subject.doorType).toLowerCase();
    phys += same ? 1 : 0.3;
    if (same) reasons.push(`same loading (${comp.door_type})`);
  }
  score += physN ? (phys / physN) * 15 : 7;   // neutral where unknown

  return {
    comp,
    score: Math.round(score),
    miles: Number.isFinite(miles) ? miles : null,
    reasons,
    // A comp can score well and still be weak evidence if the things that
    // matter most are the things we do not know.
    confident: Number.isFinite(miles) && !!year,
  };
}

/**
 * Rank the library against a subject.
 *
 * Comps already used in the underwriting are excluded from the suggestions,
 * because suggesting what is already on the page is noise.
 */
export function suggestComps(library, subject, {
  radiusMiles = 3, limit = 12, excludeIds = [],
} = {}) {
  const skip = new Set(excludeIds);
  return (library || [])
    .filter((c) => !skip.has(c.id))
    .filter((c) => Number(c.lease_rate) > 0)
    .map((c) => scoreComp(c, subject, { radiusMiles }))
    .filter((s) => s.score > 25)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * What the suggestions say about the market rent, and how strongly.
 *
 * Returns `insufficient` rather than a confident figure when the evidence is
 * thin. Our coverage is dense where we have underwritten and thin everywhere
 * else, and a tool that dresses up three old comps as a market rent is worse
 * than one that admits it does not know.
 */
export function impliedMarketRent(scored) {
  const good = scored.filter((s) => s.confident && Number(s.comp.lease_rate) > 0);
  if (good.length < 3) {
    return { rent: null, n: good.length, verdict: "insufficient evidence" };
  }
  let num = 0, den = 0;
  good.forEach((s) => {
    const w = s.score;
    num += Number(s.comp.lease_rate) * w;
    den += w;
  });
  const rent = num / den;
  const rates = good.map((s) => Number(s.comp.lease_rate)).sort((a, b) => a - b);
  return {
    rent,
    n: good.length,
    low: rates[0],
    high: rates[rates.length - 1],
    median: rates[Math.floor(rates.length / 2)],
    verdict: good.length >= 8 ? "well supported"
      : good.length >= 5 ? "supported" : "thin",
  };
}
